import { ParsedReviews } from 'stores/reviews-cache';

const ratingRegex = /data-average-rating='([\d.]+)'/;
const reviewsRegex = /data-number-of-reviews='([\d.]+)'/;

/**
 * Parse html string to retrieve ratings and reviews
 */
export const parseReviews = ({
  rawReviews,
}: {
  rawReviews: Record<string, string>;
}): ParsedReviews => {
  return Object.entries(rawReviews)
    .map((entry) => {
      const [, _ratingCount] = Array.from(entry[1].match(ratingRegex) || []);
      const ratingCount = _ratingCount;
      const [, _reviewsCount] = Array.from(entry[1].match(reviewsRegex) || []);
      const reviewsCount = _reviewsCount;

      return {
        [entry[0]]: {
          ratingCount,
          reviewsCount,
        },
      };
    })
    .reduce((prev, current) => {
      return {
        ...prev,
        ...current,
      };
    }, {});
};

/**
 * Takes a number and calculates amount of full stars, half stars and off stars from it
 * Fractional rating display rules
 * - [0.25, 0.75] → show a half star
 * - > 0.75 → round up to the next full star
 * - < 0.25 → no half star
 */
export const calculateStars = (
  rating: number
): {
  fullStars: number;
  halfStar: number;
  offStars: number;
} => {
  const MAX_STARS = 5;
  // Normalize to finite number within [0, 5]
  const normalizedRating = Math.min(Math.max(rating, 0), MAX_STARS);
  const baseFullStars = Math.floor(normalizedRating);
  const fractionalPart = normalizedRating - baseFullStars;
  let fullStars = baseFullStars;
  let halfStar = 0;

  if (fractionalPart > 0.75) {
    fullStars += 1;
  } else if (fractionalPart >= 0.25) {
    halfStar = 1;
  }

  const offStars = MAX_STARS - fullStars - halfStar;

  return { fullStars, halfStar, offStars };
};
